import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import favoritesTransitionMiddleware from './Middlewares/Favorites.middleware';
import MiniCartContainer from './Containers/MiniCart.container';
import QuickSearchContainer from './Containers/QuickSearch.container';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import DynamicComponent from './Components/DynamicComponent';
import QuickSearchSuggestions from './Components/QuickSearchSuggestions';
import { AlgoliaSearchResultsPage, AlgoliaCategoryPage, AlgoliaQuickSearch } from './Components/Algolia/Algolia';
import QuickSearchToggle from './Components/QuickSearchToggle';

// Load vanilla components
import './Containers/Vanilla.container';
import { closeSizeGuide, showSizeGuide } from './Actions/SizeGuide.action';
import { sizeGuide } from './Reducers/SizeGuide.reducer';
import { setSearchQuery } from './Actions/QuickSearch.action';
import { addOnClickEventListeners as addProductOnClickListener } from './Services/Product.service';
import { load as loadFavorites } from './Actions/Favorites.action';
import { showProductReviews } from './Actions/ProductReviews.action';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = window.__litium.store = window.__litium.store || (window.parent.__litium && window.parent.__litium.store)
    || createStore(app, preloadState, composeWithDevTools(applyMiddleware(
        thunk,
        historyMiddleware,
        favoritesTransitionMiddleware
    )));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
}

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
    });
}

const bootstrapComponents = () => {
    var miniCart = document.getElementById('miniCart');
    if (miniCart) {
        const isCheckoutPage = miniCart.dataset.isCheckoutPage === "True";
        renderReact(
            <Provider store={store}>
                <MiniCartContainer isCheckoutPage={isCheckoutPage} />
            </Provider>,
            miniCart
        );
    }

    var algoliaSearchResultsPage = document.getElementById("algolia-search-results-page");
    if (algoliaSearchResultsPage) {
        renderReact(
            <Provider store={store}>
                <AlgoliaSearchResultsPage {...window.__litium.algoliaSettings} />
            </Provider>,
            document.getElementById('algolia-search-results-page')
        )
    }

    var algoliaSearchResult = document.getElementById("algoliaSearchResult");
    if (algoliaSearchResult) {
        renderReact(
            <Provider store={store}>
                <AlgoliaQuickSearch {...window.__litium.algoliaSettings} />
            </Provider>,
            document.getElementById('algoliaSearchResult')
        )
    }

    var algoliaCategoryPage = document.getElementById("algolia-category-page");
    if (algoliaCategoryPage) {
        const algoliaSettings = window.__litium.algoliaSettings;
        const dataset = algoliaCategoryPage.dataset
        const data = {
            categoryPageId: dataset.categoryPageId,
            insightsCategory: dataset.insightsCategory,
            disableFilter: dataset.disableFilter,
            analyticsTags: dataset.analyticsTags.split(','),
            initialIndexName: dataset.initialIndexSuffix
                ? window.__litium.algoliaSettings.indexName + '_' + dataset.initialIndexSuffix
                : window.__litium.algoliaSettings.indexName,
            conditionJson: dataset.conditionJson,
            algoliaPageFilter: dataset.algoliaPageFilter,
            algoliaSettings,
        };
        renderReact(
            <Provider store={store}>
                <AlgoliaCategoryPage {...data} />
            </Provider>,
            document.getElementById('algolia-category-page')
        )
    }
    const algoliaRecommends = document.getElementsByClassName('algolia-recommend');
    if (algoliaRecommends.length > 0) {
        const AlgoliaRecommend = DynamicComponent({
            loader: () => import('./Components/Algolia/AlgoliaRecommend')
        });
        Array.from(algoliaRecommends).forEach((recommend) => {
            const recommendationType = parseInt(recommend.dataset.recommendationType);
            const maxRecommendations = parseInt(recommend.dataset.maxRecommendations);
            renderReact(
                <Provider store={store}>
                    <AlgoliaRecommend
                        {...recommend.dataset}
                        recommendationType={recommendationType}
                        maxRecommendations={maxRecommendations} />
                </Provider>,
                recommend
            );
        });
    }

    const algoliaRecommendBlocks = document.getElementsByClassName('algolia-recommend-block');
    if (algoliaRecommendBlocks.length > 0) {
        const AlgoliaRecommend = DynamicComponent({
            loader: () => import('./Components/Blocks/AlgoliaRecommendBlock')
        });
        Array.from(algoliaRecommendBlocks).forEach((recommendBlock) => {
            const recommendationType = parseInt(recommendBlock.dataset.recommendationType);
            const maxRecommendations = parseInt(recommendBlock.dataset.maxRecommendations);
            renderReact(
                <Provider store={store}>
                    <AlgoliaRecommend
                        {...recommendBlock.dataset}
                        recommendationType={recommendationType}
                        maxRecommendations={maxRecommendations} />
                </Provider>,
                recommendBlock
            );
        });
    }

    var quickSearch_Suggestions = document.getElementById("quickSearch_Suggestions");
    if (quickSearch_Suggestions) {
        renderReact(
            <Provider store={store}>
                <QuickSearchSuggestions />
            </Provider>,
            document.getElementById('quickSearch_Suggestions')
        )
    }

    var quickSearchElements = document.getElementsByClassName('quickSearch')
    if (quickSearchElements) {
        const settingsElem = document.getElementById("search-settings");
        const blocks = settingsElem ? Array.from(settingsElem.querySelector("#search-settings-blocks").children) : [];
        const settings = settingsElem ? getJsonData(settingsElem) : {};
        const algoliaSettings = window.__litium.algoliaSettings;

        if (settingsElem) {
            settingsElem.remove();
        }

        for (var elem of quickSearchElements) {
            renderReact(
                <Provider store={store}>
                    <QuickSearchContainer blocks={blocks} settings={settings} algoliaSettings={algoliaSettings} />
                </Provider>,
                elem
            );
        }
    }

    var quickSearchToggleElements = document.querySelectorAll('.quickSearchToggle');
    if (quickSearchToggleElements) {
        for (var elem of quickSearchToggleElements) {
            renderReact(
                <Provider store={store}>
                    <QuickSearchToggle />
                </Provider>,
                elem
            );
        }
    }

    var fullWidthSliderElems = document.querySelectorAll('.fullwidth-slider-block.react')
    if (fullWidthSliderElems && fullWidthSliderElems.length > 0) {
        const FullWidthSliderBlock = DynamicComponent({
            loader: () => import('./Components/Blocks/FullWidthSliderBlock')
        });

        for (var elem of fullWidthSliderElems) {
            renderReact(
                <Provider store={store}>
                    <FullWidthSliderBlock {...getJsonData(elem)} />
                </Provider>,
                elem
            );
        }
    }

    var categorySliderElem = document.getElementById('categorySlider')
    if (categorySliderElem) {
        const CategorySlider = DynamicComponent({
            loader: () => import('./Components/CategorySlider')
        });

        let items = Array.from(categorySliderElem.children).map(e => ({
            name: e.dataset["name"],
            url: e.dataset["url"],
            image: e.dataset["image"],
            active: e.dataset["active"] == "True"
        }))

        renderReact(
            <Provider store={store}>
                <CategorySlider items={items} />
            </Provider>,
            categorySliderElem
        );
    }

    var productPageImageSliderElem = document.getElementById('product-page-image-slider')
    if (productPageImageSliderElem) {
        const ProductPageImageSlider = DynamicComponent({
            loader: () => import('./Components/ProductPageImageSlider')
        });

        const images = Array.from(productPageImageSliderElem.getElementsByTagName("img")).map(img => ({
            src: img.src,
            original: img.dataset.originalSrc,
            thumbnail: img.dataset.thumbnailSrc,
            alt: img.alt,
        }))

        const videos = Array.from(productPageImageSliderElem.getElementsByTagName("video")).map(video => ({
            sourceSrc: video.querySelector("source").src,
            sourceType: video.querySelector("source").type
        }))

        renderReact(
            <Provider store={store}>
                <ProductPageImageSlider images={images} videos={videos} />
            </Provider>,
            document.getElementById('product-page-image-slider')
        );
    }

    const navbarElem = document.getElementById('navbar');
    if (navbarElem) {
        let channelPicker = document.getElementById('channel-picker');
        let countryData;
        if (channelPicker) {
            const { country } = channelPicker.dataset;
            countryData = JSON.parse(country);
        }

        let blockContainer = navbarElem.querySelector("#mega-menu-blocks")
        let blockElements = blockContainer ? Array.from(blockContainer.children) : [];
        blockContainer.remove()

        renderReact(
            <Provider store={store}>
                <NavigationContainer channelPickerItemList={countryData} blockElements={blockElements} />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    const shopTheLookButtonsElems = document.getElementsByClassName('shop-the-look__item--button--wrapper');
    if (shopTheLookButtonsElems && shopTheLookButtonsElems.length > 0) {
        const ShopTheLookButton = DynamicComponent({
            loader: () => import('./Components/ShopTheLookButton')
        });
        for (var elem of shopTheLookButtonsElems) {
            var products = getJsonData(elem) || []
            renderReact(
                <Provider store={store}>
                    <ShopTheLookButton products={products} buttonText={elem.dataset["buttonText"]} />
                </Provider>,
                elem
            )
        }
    }


    if (document.getElementById('shopTheLookContainer')) {

        const ShopTheLookContainer = DynamicComponent({
            loader: () => import('./Containers/ShopTheLookContainer')
        });
        renderReact(
            <Provider store={store}>
                <ShopTheLookContainer />
            </Provider>,
            document.getElementById('shopTheLookContainer')
        );
    }
    if (document.getElementById('priceRangeSlider')) {
        const PriceRangeSlider = DynamicComponent({
            loader: () => import('./Components/CategoryPriceRangeFilter')
        });
        renderReact(
            <Provider store={store}>
                <PriceRangeSlider />
            </Provider>,
            document.getElementById('priceRangeSlider')
        );
    }

    var productCtaBarElem = document.getElementById('product-cta-bar');
    if (productCtaBarElem) {
        const ProductPageCtaBar = DynamicComponent({
            loader: () => import('./Components/ProductPage.CtaBar')
        });
        renderReact(
            <Provider store={store}>
                <ProductPageCtaBar {...productCtaBarElem.dataset} />
            </Provider>,
            productCtaBarElem
        );
    }

    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchContainer />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Containers/PersonList.container')
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Containers/AddressList.container')
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Containers/Checkout.container')
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    const newsListElem = document.getElementById('news-list-container');

    if (newsListElem) {
        const newsListData = newsListElem.dataset["newsItems"];
        let parsedNewsListData = [];
        if (newsListData) {
            parsedNewsListData = JSON.parse(newsListData);
        }
        const NewsListContainer = DynamicComponent({
            loader: () => import('./Containers/Newslist.container')
        });
        renderReact(
            <Provider store={store}>
                <NewsListContainer news={parsedNewsListData} />
            </Provider>,
            newsListElem
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Containers/LightboxImages.container')
        });
        const rootElement = document.getElementById('lightBoxImages');
        const images = Array.from(rootElement.querySelectorAll('a')).map(img => ({ src: img.dataset.src }));
        const thumbnails = Array.from(rootElement.querySelectorAll('a img')).map(img => ({ src: img.src }));
        renderReact(
            <Provider store={store}>
                <LightboxImages images={images} thumbnails={thumbnails} />
            </Provider>,
            document.getElementById('lightBoxImages')
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider')
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('.slider__block')].map(block => {
                return ({
                    image: block.dataset.image,
                    url: block.dataset.url,
                    text: block.dataset.text,
                })
            });
            if (values.length > 0) {
                renderReact(
                    <Slider values={values} />,
                    slider
                );
            }
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.Container')
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { isInStock, articleNumber, quantityFieldId, href, cssClass, label, outOfStockLabel } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton {...{ isInStock, label, outOfStockLabel, articleNumber, quantityFieldId, href, cssClass }} />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.Container')
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach((button) => {
            const { cssClass, orderId, title } = button.dataset;
            const label = button.innerText;
            renderReact(
                <Provider store={store}>
                    <ReorderButton {...{ label, title, cssClass, orderId }} />
                </Provider>,
                button
            );
        });
    }

    let topMenuElem = document.getElementById("top-menu-announcement-text")
    if (topMenuElem) {
        const TopMenuAnnouncementText = DynamicComponent({
            loader: () => import('./Components/TopMenuAnnouncementText')
        })
        let children = Array.from(topMenuElem.children)
        let items = children ? children.map(a => ({ href: a.href, text: a.innerText })) : undefined
        let color = topMenuElem.dataset["color"]
        if (color == "") {
            color = null
        }

        renderReact(
            <Provider store={store}>
                <TopMenuAnnouncementText items={items} duration={topMenuElem.dataset["duration"]} color={color}></TopMenuAnnouncementText>
            </Provider>,
            topMenuElem
        )
    }

    let sizePickerElem = document.getElementById("size-picker")
    if (sizePickerElem) {
        const SizePickerContainer = DynamicComponent({
            loader: () => import('./Containers/SizePicker.container')
        })

        renderReact(
            <Provider store={store}>
                <SizePickerContainer />
            </Provider>,
            sizePickerElem
        )
    }
    let findStoreElem = document.getElementById("find-store")
    if (findStoreElem) {
        const FindStoreComponent = DynamicComponent({
            loader: () => import('./Containers/FindStore.container')
        })
        const { findStoreModel } = findStoreElem.dataset

        renderReact(
            <Provider store={store}>
                <FindStoreComponent findStoreModel={findStoreModel} />
            </Provider>,
            findStoreElem
        )
    }

    let loadMoreBtnElem = document.getElementById("load-more-button")
    if (loadMoreBtnElem) {
        const LoadMoreProductsContainer = DynamicComponent({
            loader: () => import("./Containers/LoadMoreProducts.container")
        })

        const dataset = loadMoreBtnElem.dataset
        const data = {
            totalProducts: parseInt(dataset.totalProducts),
            currentProductsCount: parseInt(dataset.currentProductsCount),
            take: parseInt(dataset.takeCount),
            type: dataset.type,
            identifier: dataset.identifier,
            loadMoreTotalText: loadMoreBtnElem.innerText
        }

        renderReact(
            <Provider store={store}>
                <LoadMoreProductsContainer {...data} />
            </Provider>,
            loadMoreBtnElem
        )
    }

    let productSlideElems = document.getElementsByClassName("product-slider")
    if (productSlideElems && productSlideElems.length > 0) {
        const ProductSlider = DynamicComponent({
            loader: () => import('./Components/ProductSlider')
        })

        for (var elem of productSlideElems) {
            const products = Array.from(elem.children).map(p => {
                let gtmProduct = null;
                try {
                    const jsonDataElement = p.querySelector("json-data.gtm-product");
                    gtmProduct = jsonDataElement ? JSON.parse(jsonDataElement.innerText) : null;
                } catch (error) {
                    console.warn("Failed to parse gtmProduct", error);
                }
                return ({
                    name: p.dataset["name"],
                    brand: p.dataset["brand"],
                    decsription: p.dataset["description"],
                    url: p.dataset["url"],
                    price: p.dataset["price"],
                    campaignPrice: p.dataset["campaignPrice"],
                    discountPercent: p.dataset["discountPercent"],
                    image1: p.dataset["image1"],
                    image2: p.dataset["image2"],
                    articleNumber: p.dataset["articleNumber"],
                    show: JSON.parse(p.dataset["show"]),
                    gtmProduct: gtmProduct
                });
            })

            renderReact(
                <Provider store={store}>
                    <ProductSlider {...{ products }}></ProductSlider>
                </Provider>,
                elem
            )
        }
    }


    let quickAddElems = document.getElementsByClassName("product__quick-add-container")
    if (quickAddElems && quickAddElems.length > 0) {
        const QuickAdd = DynamicComponent({
            loader: () => import('./Components/ProductCard.QuickAdd')
        })

        for (var quickAddElem of quickAddElems) {
            const sizeModels = Array.from(quickAddElem.children).map(e => e.dataset)
            quickAddElem.dataset["loaded"] = true;
            renderReact(
                <Provider store={store}>
                    <QuickAdd sizeModels={sizeModels} sizeHintValue={quickAddElem.dataset["sizeHintValue"]} sizeHintShort={quickAddElem.dataset["sizeHintShort"]} />
                </Provider>,
                quickAddElem
            )
        }
    }

    let mobileBtnElem = document.getElementById("mobile-nav-btn")
    if (mobileBtnElem) {
        const MobileNavigationButton = DynamicComponent({
            loader: () => import('./Components/Navigation/MobileNavigationButton')
        })

        renderReact(
            <Provider store={store}>
                <MobileNavigationButton />
            </Provider>,
            mobileBtnElem
        )
    }

    let overlayContainerElem = document.getElementById("overlayContainer")
    if (overlayContainerElem) {
        const OverlayContainer = DynamicComponent({
            loader: () => import("./Containers/Overlay.container")
        })

        renderReact(
            <Provider store={store}>
                <OverlayContainer />
            </Provider>,
            overlayContainerElem
        )
    }

    let sizeGuideElem = document.getElementById("size-guide-content")
    if (sizeGuideElem) {
        const SizeGuideContainer = DynamicComponent({
            loader: () => import("./Containers/SizeGuide.container")
        })

        renderReact(
            <Provider store={store}>
                <SizeGuideContainer>
                    {Array.from(sizeGuideElem.children)}
                </SizeGuideContainer>
            </Provider>,
            sizeGuideElem
        )
    }

    let sizeGuideBtns = document.getElementsByClassName("size-guide-btn")
    if (sizeGuideBtns && sizeGuideBtns.length > 0) {
        for (let btn of sizeGuideBtns) {
            btn.addEventListener("click", e => {
                e.preventDefault()
                store.dispatch(showSizeGuide())
            })
        }
    }

    let brandList = document.getElementById("brand-list")
    if (brandList) {
        const BrandListContainer = DynamicComponent({
            loader: () => import("./Containers/BrandListContainer")
        })
        let title = brandList.dataset["title"];
        let brands = brandList.dataset["brands"];
        let blocks = brandList.querySelector("#blocks");

        renderReact(
            <Provider store={store}>
                <BrandListContainer brands={brands} title={title}>
                    {Array.from(blocks.children)}
                </BrandListContainer>
            </Provider>,
            brandList
        );
    }

    const channelPicker = document.getElementById('channel-picker');
    if (channelPicker) {
        const { country } = channelPicker.dataset;
        const ChannelPicker = DynamicComponent({
            loader: () => import('./Containers/ChannelPicker.container')
        });
        ReactDOM.render(
            <Provider store={store}>
                <ChannelPicker itemList={JSON.parse(country)} />
            </Provider>, channelPicker
        );
    }

    const flowBox = document.getElementsByClassName('flowbox-react');
    if (flowBox.length > 0) {
        const FlowBoxContainer = DynamicComponent({
            loader: () => import('./Containers/Flowbox.container')
        });
        Array.from(flowBox).forEach((item, index) => {
            const { apikey, container, locale, tags, tags2 } = item.dataset;
            ReactDOM.render(
                <Provider store={store}>
                    <FlowBoxContainer apikey={apikey} container={container} locale={locale} tags={tags && tags.split(',')} tags2={tags2 && tags2.split(',')} />
                </Provider>,
                item
            );
        });
    }

    const backInStockPanelController = document.getElementById('backinstockpanel-controller');
    if (backInStockPanelController) {
        const BackInStockPanelContainer = DynamicComponent({
            loader: () => import('./Containers/BackInStockPanel.container')
        });
        ReactDOM.render(
            <Provider store={store}>
                <BackInStockPanelContainer />
            </Provider>, backInStockPanelController
        );
    }

    render("#filter-bar-show-filters", DynamicComponent({
        loader: () => import("./Components/FilterBar/FilterBar.FilterMenu")
    }))

    render("#filter-bar-scroll-top", DynamicComponent({
        loader: () => import("./Components/FilterBar/FilterBar.ScrollTopButton")
    }))

    render("#filter-bar-loading-spinner", DynamicComponent({
        loader: () => import("./Components/FilterBar/FilterBar.LoadingSpinner")
    }));

    render("#search-result-filtering", DynamicComponent({
        loader: () => import("./Components/SearchResultPage.Filtering")
    }), elem => ({ items: Array.from(elem.children).map(c => c.dataset) }))

    for (let productListElem of document.querySelectorAll(".main-content [data-productlist]")) {
        addProductOnClickListener(productListElem);
    }

    const productPriceHistoryLinks = document.querySelectorAll('product-price-history-link');
    if (productPriceHistoryLinks.length > 0) {
        const ProductPriceHistoryLink = DynamicComponent({
            loader: () => import('./Components/ProductPriceHistory/ProductPriceHistoryLink'),
        });
        Array.from(productPriceHistoryLinks).forEach((productPriceHistoryLink) => {
            const {
                title,
                price,
                description
            } = productPriceHistoryLink.dataset;
            renderReact(
                <Provider store={store}>
                    <ProductPriceHistoryLink
                        {...{
                            title,
                            price,
                            description
                        }}
                    />
                </Provider>,
                productPriceHistoryLink
            );
        });
    }
    render("#cookie-consent", DynamicComponent({
        loader: () => import("./Components/CookieConsent")
    }), elem => JSON.parse(elem.querySelector("json-data").innerText));

    render("#faceted-pagination", DynamicComponent({
        loader: () => import("./Components/FacetedPagination")
    }));

    // Mount FavoriteButton on server-rendered elements
    const favoriteButtonElems = document.querySelectorAll('.favorite-button-react');
    if (favoriteButtonElems.length > 0) {
        const FavoriteButton = DynamicComponent({
            loader: () => import('./Components/FavoriteButton')
        });
        favoriteButtonElems.forEach(elem => {
            // Pass data-item attribute as prop to FavoriteButton
            const item = elem.dataset.item;
            const productData = {
                productName: elem.dataset.productName || undefined,
                brand: elem.dataset.brand || undefined,
                price: elem.dataset.price || undefined,
                imageUrl: elem.dataset.imageUrl || undefined,
                url: elem.dataset.url || undefined
            };
            renderReact(
                <Provider store={store}>
                    <FavoriteButton item={item} productData={productData} />
                </Provider>,
                elem
            );
        });
    }

    const fitRatingSummaryElems = document.querySelectorAll('.fit-rating-summary-react');
    if (fitRatingSummaryElems.length > 0) {
        const FitRatingSummary = DynamicComponent({
            loader: () => import('./Components/FitRatingSummary')
        });
        fitRatingSummaryElems.forEach(elem => {
            elem.addEventListener('click', (e) => {
                e.preventDefault();

                if (!elem.querySelector('.fit-rating-summary--has-rating')) return;
                store.dispatch(showProductReviews());
            });

            const choiceno = elem.dataset.choiceno;
            const tyckaProjectId = elem.dataset.tyckaprojectid;
            const sizeHint = elem.dataset.sizehint;
            const showSizeGuide = elem.dataset.showsizeguide === 'True';
            renderReact(
              <Provider store={store}>
                <FitRatingSummary choiceNo={choiceno} tyckaProjectId={tyckaProjectId} sizeHint={sizeHint} showSizeGuide={showSizeGuide} />
              </Provider>,
                elem
            );
        });
    }

    // Mount FavoriteCountButton on server-rendered elements
    const favoriteCountButtonElems = document.querySelectorAll('.favorite-count-button-react');
    if (favoriteCountButtonElems.length > 0) {
        const FavoriteCountButton = DynamicComponent({
            loader: () => import('./Components/FavoriteCountButton')
        });
        favoriteCountButtonElems.forEach(elem => {
            renderReact(
                <Provider store={store}>
                    <FavoriteCountButton />
                </Provider>,
                elem
            );
        });
    }

    const productReviewElem = document.querySelector('.product-detail__reviews');
    if (productReviewElem) {
        productReviewElem.addEventListener('click', (e) => {
            e.preventDefault();
            store.dispatch(showProductReviews());
        });
    }

    const productReviewsElem = document.getElementById("product-reviews")
    if (productReviewsElem) {
        const ProductReviewsContainer = DynamicComponent({
            loader: () => import("./Containers/ProductReviews.container")
        })

        renderReact(
            <Provider store={store}>
                <ProductReviewsContainer {...productReviewsElem.dataset} />
            </Provider>,
            productReviewsElem
        )
    }

    window.addEventListener("tycka-average-rating-fetched", (e) => {
        if (e.detail.numberOfReviews || !productReviewElem) return;

        productReviewElem.classList.add("product-detail__reviews--disabled");
    });

}

bootstrapComponents();

store.dispatch(loadFavorites());

function getJsonData(elem) {
    const jsonDataElem = elem.querySelector("json-data")
    if (jsonDataElem) {
        return JSON.parse(jsonDataElem.innerText)
    }
}

function render(selector, Component, propsFunc = () => { }) {
    (async () => {
        const container = document.querySelector(selector)
        if (!container) return
        const props = propsFunc(container)
        renderReact(<Provider store={store}>
            <Component {...props} />
        </Provider>, container)
    })()
}

function renderMany(selector, Component, propsFunc = () => { }) {
    (async () => {
        const containers = document.querySelectorAll(selector)
        if (!containers) return
        for (let container of containers) {
            const props = propsFunc(container)
            renderReact(<Provider store={store}>
                <Component {...props} />
            </Provider>, container)
        }
    })()
}
